import { Component, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatToolbarModule } from "@angular/material/toolbar";
import { RequestService } from "@services/request/request.service";

const ALLOWED_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif", "svg"];
const MAX_SIZE_MB = 10;

interface IUploadResponse {
  result_code: number;
  message: string;
  data: { url: string } | null;
}

interface IUploadedImage {
  key: string;
  url: string;
  size: number;
  lastModified: string | null;
}

interface IListResponse {
  result_code: number;
  message: string;
  data: { images: IUploadedImage[] } | null;
}

interface IDeleteResponse {
  result_code: number;
  message: string;
  data: null;
}

@Component({
  selector: "fh-media-uploader",
  templateUrl: "./media-uploader.component.html",
  styleUrls: ["./media-uploader.component.scss"],
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatToolbarModule],
})
export class MediaUploaderComponent implements OnInit {
  isDragging = false;
  isUploading = false;
  errorMessage: string | null = null;

  preview: string | null = null;
  cdnUrl: string | null = null;
  copied = false;

  images: IUploadedImage[] = [];
  isLoadingImages = false;
  copiedKey: string | null = null;
  deletingKey: string | null = null;

  constructor(private request: RequestService) {}

  ngOnInit(): void {
    this.loadImages();
  }

  loadImages(): void {
    this.isLoadingImages = true;
    this.request.get<IListResponse>("/api/v1/image/static-list").subscribe({
      next: (res) => {
        this.isLoadingImages = false;
        this.images = res.result_code === 1 ? res.data?.images || [] : [];
      },
      error: () => {
        this.isLoadingImages = false;
      },
    });
  }

  copyImageLink(image: IUploadedImage): void {
    navigator.clipboard.writeText(image.url).then(() => {
      this.copiedKey = image.key;
      setTimeout(() => (this.copiedKey = null), 2000);
    });
  }

  deleteImage(image: IUploadedImage): void {
    if (!confirm(`Delete this image? This can't be undone.\n\n${image.key}`)) return;

    this.deletingKey = image.key;
    this.request
      .delete<IDeleteResponse>(`/api/v1/image/static-upload?key=${encodeURIComponent(image.key)}`)
      .subscribe({
        next: (res) => {
          this.deletingKey = null;
          if (res.result_code === 1) {
            this.images = this.images.filter((i) => i.key !== image.key);
          } else {
            this.errorMessage = res.message || "Delete failed";
          }
        },
        error: () => {
          this.deletingKey = null;
          this.errorMessage = "Delete failed. Please try again.";
        },
      });
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.handleFile(file);
  }

  onFileInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) this.handleFile(file);
    target.value = "";
  }

  private handleFile(file: File): void {
    this.errorMessage = null;
    this.cdnUrl = null;
    this.copied = false;

    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      this.errorMessage = `Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`;
      return;
    }
    if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
      this.errorMessage = `Image must be ${MAX_SIZE_MB}MB or smaller`;
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.preview = reader.result as string;
      this.upload(reader.result as string, ext, file.name);
    };
    reader.readAsDataURL(file);
  }

  private upload(base64Image: string, ext: string, fileName: string): void {
    this.isUploading = true;
    this.request
      .post<IUploadResponse>("/api/v1/image/static-upload", {
        image: base64Image,
        imageExt: ext,
        fileName,
      })
      .subscribe({
        next: (res) => {
          this.isUploading = false;
          if (res.result_code === 1 && res.data?.url) {
            this.cdnUrl = res.data.url;
            this.loadImages();
          } else {
            this.errorMessage = res.message || "Upload failed";
          }
        },
        error: () => {
          this.isUploading = false;
          this.errorMessage = "Upload failed. Please try again.";
        },
      });
  }

  copyLink(): void {
    if (!this.cdnUrl) return;
    navigator.clipboard.writeText(this.cdnUrl).then(() => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    });
  }

  reset(): void {
    this.preview = null;
    this.cdnUrl = null;
    this.errorMessage = null;
    this.copied = false;
  }
}
